import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { displayStringFromLang, emojiFromLang, uiLanguages } from "./locales/locales";
import { useIsHardNo } from "./util/useDomain";
import { Spacer } from "./editor/EditorHelpers";

export default function Nav() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isLangMenuOpen, setLangMenuOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const navlinksRef = useRef(null);
  const hamburgerRef = useRef(null);
  const { isHardNo } = useIsHardNo();

  const navLogoSrc = isHardNo
    ? '/hardno_header.png'
    : '/boycott_flyer_header.png';

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setLangMenuOpen(false);
      }
    };

    if (isLangMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLangMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navlinksRef.current &&
        !navlinksRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const isChallengeActive = pathname.startsWith('/challenge');
  const isAboutActive = pathname.startsWith('/about');
  const isKitActive = !isChallengeActive && !isAboutActive;

  return (
    <NavContainer>
      <Container className="Container">
        <LogoLink to="/"><NavLogo src={navLogoSrc} /></LogoLink>
        <Hamburger ref={hamburgerRef} onClick={() => setMenuOpen(!isMenuOpen)}>☰</Hamburger>
        <NavLinks ref={navlinksRef} $isMenuOpen={isMenuOpen}>
          <StyledLink to="/" $active={isKitActive}>{t('kit.nav')}</StyledLink>
          <StyledLink to="/challenge" $active={isChallengeActive}>{t('challenge.nav')}</StyledLink>
          <StyledLink to="/about" $active={isAboutActive}>{t('about-us.nav')}</StyledLink>
          <Spacer style={{ flexGrow: 1}} />
          {uiLanguages.length > 1 && (
            <LanguagePickerWrapper>
              <LanguagePickerButton ref={buttonRef} onClick={() => setLangMenuOpen(!isLangMenuOpen)}>
                {emojiFromLang(i18n.language)}
              </LanguagePickerButton>
              <DropdownMenu ref={dropdownRef} $isOpen={isLangMenuOpen}>
                {uiLanguages.map((langCode) => (
                  <li key={langCode} onClick={() => handleLanguageChange(langCode)}>
                    {emojiFromLang(langCode)} {displayStringFromLang(langCode)}
                  </li>
                ))}
              </DropdownMenu>
            </LanguagePickerWrapper>
          )}
        </NavLinks>
      </Container>
    </NavContainer>
  );
}

const NavContainer = styled.nav`
  background: var(--primary);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  align-items: center;
  justify-content: space-between;
  margin: auto;
  padding: 5px 20px 5px 5px;
  position: relative;
`;

const LogoLink = styled(Link)`
  display: flex;
  padding: 0px 10px 0px 0px;
  justify-content: center;
  align-items: center;
`;

const NavLogo = styled.img`
  height: 24px;
`;

const NavLinks = styled.div<{ $isMenuOpen: boolean }>`
  display: flex;
  align-items: center;
  flex-grow: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--primary);
    display: ${({ $isMenuOpen }) => ($isMenuOpen ? 'flex' : 'none')};
    padding: 10px 0;
    border-bottom: 1px solid var(--border-color);
  }
`;

const StyledLink = styled(Link)<{ $active }>`
  padding: 6px 6px 6px 6px;
  text-decoration: none;
  color: ${({ $active }) => ($active ? 'var(--on-primary-active)' : 'var(--on-primary)')};
  &:hover { color: ${({ $active }) => ($active ? 'var(--on-primary-active)' : 'var(--on-primary-hover)')}; }
`;

const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--on-primary);
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const LanguagePickerWrapper = styled.div`
  position: relative;
`;

const LanguagePickerButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
`;

const DropdownMenu = styled.ul<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%; 
  right: 0; 
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  background-color: var(--bg-color);
  color: var(--text-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};

  li {
    display: flex; 
    align-items: center; 
    white-space: nowrap; 
    padding: 10px 20px;
    cursor: pointer;
   &:hover {
      background-color: var(--bg-color-hover);
      border-radius: 8px;
    }
  }
`;

